import { Injectable, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { IRequestOptions } from '@core/interfaces/api-request-options.interface';
import { ApiService } from '@core/services/api.service';
import { ApiServiceHolder } from '@core/utils/api-service-holder';
import { UntilDestroy } from '@ngneat/until-destroy';
import { CategoriesView } from '@shared/interfaces/api.interface';
import { IKeyValue } from '@shared/interfaces/key-value.interface';
import {
  Catalog,
  FilterProperty,
  FilterPropertyValue,
  FilterStatus,
  GetAllFilterPropertiesDto,
  IAuctionFilterDto,
} from '@shared/interfaces/search.interface';
import { Observable, Subject, map, throwError } from 'rxjs';
import {
  ArticleProperty,
  AuctionView,
  AuctionViewMinimal,
} from 'src/app/articles-manager/interfaces/auction.interface';
import { environment } from 'src/environments/environment';
import { LanguageService } from './language.service';


@Injectable({
  providedIn: 'root',
})
@UntilDestroy()
export class SearchService {
  auctionTypeList: IKeyValue[] = [];
  sportList: IKeyValue[] = [];
  competitionList: IKeyValue[] = [];
  teamList: IKeyValue[] = [];
  filtersChange = new Subject<{}>();
  lastResult: AuctionView[] = [];
  lastSearch: Date;

  private readonly languageService = inject(LanguageService);

  lastResultByType: {
    [key: string]: {
      [key: number]: {
        lastResult: AuctionView[];
        lastSearch: Date;
        lastFilters: IAuctionFilterDto;
      };
    };
  } = {};
  filters: IAuctionFilterDto = {
    idLanguage: -1,
    searchText: '',
    idAuctionType: -1,
    idDetail: null,
    onlySigned: false,
    idSport: null,
    idCompetition: null,
    idTeam: null,
    onlyNew: false,
    onlyExpiring: false,
    onlyArriving: false,
    onlyBuyNow: false,
    auctionStatus: FilterStatus.OnlyActive,
    idEntity: null,
    idCampaign: null,
    idPromoter: null,
    idActiveAuction: null,
    idTagList: [],
    catalog: Catalog.All,
    orderBy: 'evidence',
    orderDirection: 'desc',
  };
  searchRoute: string = '';

  private mapImageAuctionView = (auctions: AuctionViewMinimal[]) =>
    auctions.map(
      (a) =>
        ({
          ...a,
          articlePhoto: {
            fullPath: environment.ImageUrl + a.imagePath,
          } as any,
        } satisfies Partial<AuctionView>)
    ) as AuctionView[];

  constructor(
    private readonly _api: ApiService,
    private readonly router: Router,
    private readonly route: ActivatedRoute
  ) {
    this.route.queryParams.subscribe((params) => {
      this.filters.searchText = params['searchText'] || '';
      this.filters.idAuctionType = params['idAuctionType']
        ? +params['idAuctionType']
        : -1;
      this.filters.idSport = params['idSport'] ? +params['idSport'] : -1;
      this.filters.idCompetition = params['idCompetition']
        ? +params['idCompetition']
        : -1;
      this.filters.idTeam = params['idTeam'] ? +params['idTeam'] : -1;
      this.filters.onlyNew = params['onlyNew'] === 'true';
      this.filters.onlyExpiring = params['onlyExpiring'] === 'true';
      this.filters.onlyArriving = params['onlyArriving'] === 'true';
      this.filters.onlyFavorites = params['onlyFavorites'] === 'true';
      this.filters.onlySigned = params['onlySigned'] === 'true';
      this.filters.onlyBuyNow = params['onlyBuyNow'] === 'true';
      this.filters.auctionStatus = params['auctionStatus']
        ? +params['auctionStatus']
        : this.router.url.includes('/tags/')
        ? FilterStatus.All
        : FilterStatus.OnlyActive;

      this.filters.idLanguage =
        this.languageService.userSelectedLanguage?.idLanguage ?? -1;

      if (this.filters.onlyArriving) {
        this.filters.auctionStatus = FilterStatus.All;
      }
    });

    this.languageService.selectedLanguage$.subscribe((language) => {
      if (
        language &&
        this.filters.idLanguage !== language.idLanguage &&
        this.filters.idLanguage !== -1
      ) {
        this.setFilters(this.searchRoute, {
          ...this.filters,
          idLanguage: language.idLanguage,
        });
      }
    });
  }

  setFilters(nextRoute: string, filters: IAuctionFilterDto) {
    this.filters = filters;
    if (this.searchRoute == nextRoute) {
      this.filtersChange.next({});
    } else {
      this.searchRoute = nextRoute;
      this.router.navigate([this.searchRoute], { relativeTo: this.route });
    }
    this.updateQueryParams(filters);
  }

  listenFiltersChanges(): Observable<any> {
    return this.filtersChange.asObservable();
  }

  getAllV1(filters: IAuctionFilterDto): Observable<AuctionView[]> {
    return this._api.request<AuctionView[]>(
      ApiServiceHolder.auctionView.getAll(filters)
    );
  }

  getAllV2(filters: IAuctionFilterDto): Observable<AuctionView[]> {
    return this._api
      .request<AuctionViewMinimal[]>(
        ApiServiceHolder.auctionView.getAllV2(filters)
      )
      .pipe(map(this.mapImageAuctionView));
  }

  getAllV2Cached(filters: IAuctionFilterDto): Observable<AuctionView[]> {
    return this._api
      .requestCached<AuctionViewMinimal[]>(
        ApiServiceHolder.auctionView.getAllV2(filters)
      )
      .pipe(map(this.mapImageAuctionView));
  }

  getAllCached(filters: IAuctionFilterDto): Observable<AuctionView[]> {
    return this._api.requestCached<AuctionView[]>(
      ApiServiceHolder.auctionView.getAll(filters)
    );
  }

  getSuggested(
    idLanguage: number,
    idAuction: number
  ): Observable<{ memoraBid: AuctionView[]; marketPlace: AuctionView[] }> {
    return this._api.requestLocalStorageCached<{
      memoraBid: AuctionView[];
      marketPlace: AuctionView[];
    }>(
      ApiServiceHolder.auctionView.getSuggested(idLanguage, idAuction),
      60 * 60_000
    ); // cache for 1 hour
  }

  getAllAndCountV1(
    filters: IAuctionFilterDto
  ): Observable<{ count: number; auctions: AuctionView[] }> {
    return this._api.request<{ count: number; auctions: AuctionView[] }>(
      ApiServiceHolder.auctionView.getAllAndCountV2(filters)
    );
  }

  getAllAndCountCachedV1(
    filters: IAuctionFilterDto
  ): Observable<{ count: number; auctions: AuctionView[] }> {
    return this._api.requestCached<{ count: number; auctions: AuctionView[] }>(
      ApiServiceHolder.auctionView.getAllAndCountV1(filters)
    );
  }

  getAllAndCountV2(
    filters: IAuctionFilterDto
  ): Observable<{ count: number; auctions: AuctionView[] }> {
    return this._api
      .request<{ count: number; auctions: AuctionViewMinimal[] }>(
        ApiServiceHolder.auctionView.getAllAndCountV2(filters)
      )
      .pipe(
        map((a) => ({
          count: a.count,
          auctions: this.mapImageAuctionView(a.auctions),
        }))
      );
  }

  getAllAndCountCachedV2(
    filters: IAuctionFilterDto
  ): Observable<{ count: number; auctions: AuctionView[] }> {
    return this._api
      .requestCached<{ count: number; auctions: AuctionViewMinimal[] }>(
        ApiServiceHolder.auctionView.getAllAndCountV2(filters)
      )
      .pipe(
        map((a) => ({
          count: a.count,
          auctions: this.mapImageAuctionView(a.auctions),
        }))
      );
  }

  getAllForUser(
    idLanguage: number,
    userAuctionFilter: 'onlystarred' | 'onlywin' | 'onlylost'
  ): Observable<AuctionView[]> {
    let apiOptions: IRequestOptions;
    switch (userAuctionFilter) {
      case 'onlystarred':
        apiOptions = ApiServiceHolder.auction.getAllForUserStarred(idLanguage);
        break;
      case 'onlywin':
        apiOptions = ApiServiceHolder.auction.getAllForUserWin(idLanguage);
        break;
      case 'onlylost':
        apiOptions = ApiServiceHolder.auction.getAllForUserLost(idLanguage);
        break;
      default:
        throwError(() => 'No type selected.');
        break;
    }
    return this._api.request<AuctionView[]>(apiOptions);
  }

  getAllAuction(idLanguage: number): Observable<AuctionView[]> {
    return this._api.request(ApiServiceHolder.auction.getAll(idLanguage));
  }

  getAllTypes(): Observable<IKeyValue[]> {
    return this._api.request(ApiServiceHolder.filter.getAllTypes());
  }

  getAllFilterProperties(idArticleType: number): Observable<FilterProperty[]> {
    return this._api.request(
      ApiServiceHolder.filter.getAllFilterProperties(idArticleType)
    );
  }

  getFilterPropertyValue(
    itemOrder: number,
    value: string
  ): Observable<FilterPropertyValue[]> {
    return this._api.request(
      ApiServiceHolder.filter.getFilterPropertyValue(itemOrder, value)
    );
  }

  getAllProperties(
    filterProperties: GetAllFilterPropertiesDto
  ): Observable<ArticleProperty[]> {
    return this._api.request(
      ApiServiceHolder.filter.getAllProperties(filterProperties)
    );
  }

  getRelatedTagCategories(idTag: number, onlyActive: boolean = true) {
    return this._api.requestCached<Array<CategoriesView>>(
      ApiServiceHolder.categories.getRelatedTagCategories(idTag, onlyActive)
    );
  }
  getRelatedTagSellerCategories(
    idTag: number | null,
    idSeller: number | null,
    onlyActive: boolean = true
  ) {
    return this._api.requestCached<Array<CategoriesView>>(
      ApiServiceHolder.categories.getRelatedTagSellerCategories(
        idTag ?? -1,
        idSeller ?? -1,
        onlyActive
      )
    );
  }

  initializeRoute(url: string) {
    const filters: IAuctionFilterDto = {
      ...this.filters,
      searchText: '',
      idAuctionType: -1,
      idSport: -1,
      idCompetition: -1,
      idTeam: -1,
      onlyNew: false,
      onlyExpiring: false,
      onlyArriving: false,
      onlyFavorites: false,
      onlySigned: false,
      onlyBuyNow: false,
      idDetail: null,
      idTagList: [],
      idActiveAuction: null,
      idCampaign: null,
      idCategoriesList: [],
      idEntity: null,
      idPromoter: null,
      orderBy: 'evidence',
      orderDirection: 'desc',
      auctionStatus: FilterStatus.OnlyActive,
    };

    const params = url.split('/');
    const currentPageRoute = params[params.length - 1];
    this.searchRoute = currentPageRoute;
    switch (currentPageRoute) {
      case 'search-result-signed': {
        filters.onlySigned = true;
        this.filters = filters;
        break;
      }
      case 'search-result-store': {
        filters.idDetail = 1;
        this.filters = filters;
        break;
      }
      case 'search-result-worn': {
        filters.idDetail = 2;
        this.filters = filters;
        break;
      }
      case 'search-result-all': {
        this.filters = filters;
        break;
      }
      default: {
        if (this.filters.idSport == null) {
          this.filters.idSport = -1;
        }
        if (this.filters.idCompetition == null) {
          this.filters.idCompetition = -1;
        }
        if (this.filters.idTeam == null) {
          this.filters.idTeam = -1;
        }
        break;
      }
    }
  }

  updateQueryParams(filter: IAuctionFilterDto) {
    let auctionStatus =
      filter.auctionStatus !==
      (this.router.url.includes('/tags/')
        ? FilterStatus.All
        : FilterStatus.OnlyActive)
        ? filter.auctionStatus
        : null;

    if (filter.onlyArriving) {
      auctionStatus = FilterStatus.All;
    }

    const queryParams: any = {
      searchText: filter.searchText ? filter.searchText : null,
      idAuctionType: filter.idAuctionType !== -1 ? filter.idAuctionType : null,
      idSport: filter.idSport !== -1 ? filter.idSport : null,
      idCompetition: filter.idCompetition !== -1 ? filter.idCompetition : null,
      idTeam: filter.idTeam !== -1 ? filter.idTeam : null,
      onlyNew: filter.onlyNew ? filter.onlyNew : null,
      onlyExpiring: filter.onlyExpiring ? filter.onlyExpiring : null,
      onlyArriving: filter.onlyArriving ? filter.onlyArriving : null,
      onlyFavorites: filter.onlyFavorites ? filter.onlyFavorites : null,
      onlySigned: filter.onlySigned ? filter.onlySigned : null,
      onlyBuyNow: filter.onlyBuyNow ? filter.onlyBuyNow : null,
      auctionStatus,
    };

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
      // skipLocationChange: true,
    });
  }
}
